/* eslint-disable sonarjs/cognitive-complexity */
import {SlotsPlaceholder} from '@wix/widget-plugins-ooi';
import {ShowOnDesktopOnly} from '@wix/wixstores-client-common-components/dist/es/src/HOC/responsive/ShowOnDesktopOnly/ShowOnDesktopOnly';
import {ShowOnMobileOnly} from '@wix/wixstores-client-common-components/dist/es/src/HOC/responsive/ShowOnMobileOnly/ShowOnMobileOnly';
import {ImageMode} from '@wix/wixstores-client-core/dist/es/src/media/constants';
import {getMainImageRatio} from '@wix/wixstores-client-core/dist/es/src/media/mediaService';
import cx from 'classnames';
import md5 from 'md5';
import React from 'react';
import {
  GalleryNavigationLocation,
  GalleryNavigationPosition,
  GalleryNavigationType,
  Layout,
  ProductPageSlotIds,
} from '../../../constants';
import {ProvidedGlobalProps, withGlobalProps} from '../../../providers/globalPropsProvider';
import {SPECS} from '../../../specs';
import {IProductDTO, LayoutConfig} from '../../../types/app-types';
import {ModalGalleryLayout} from '../../ModalGallery/ModalGalleryLayout';
import {Mode, WishlistButton} from '../../ProductPageButtonsContainer/WishlistButton/WishlistButton';
import {MainMedia} from '../MainMedia/MainMedia';
import {MediaNavigationArrows} from '../MediaNavigationArrows/MediaNavigationArrows';
import {MediaOverlay} from '../MediaOverlay/MediaOverlay';
import {ProductGalleryNavigation} from '../ProductGalleryNavigation/ProductGalleryNavigation';
import {ProductGalleryContext, ProductGalleryProvider} from '../ProductGalleryProvider/ProductGalleryProvider';
import style from './ProductGalleryLayout.scss';

export interface ProductGalleryLayoutProps extends ProvidedGlobalProps {
  layoutConfig: LayoutConfig;
  imageMode: ImageMode;
  imageRatioId: number;
  navigationType: GalleryNavigationType;
  navigationPosition: GalleryNavigationPosition;
  navigationLocation: GalleryNavigationLocation;
  layout: Layout;
}

export const enum ProductGalleryLayoutDataHook {
  Root = 'product-gallery-root',
  MainMediaContainer = 'main-media-container',
  MediaNavigation = 'product-media-navigation-root',
}

class ProductGalleryLayoutComponent extends React.Component<ProductGalleryLayoutProps> {
  private imageDimensionFallback = {height: 1, width: 1};

  public componentDidMount(): void {
    const {product} = this.props.globals;

    /* istanbul ignore next: todo: test */
    (product.media[0]?.height <= 1 || product.media[0]?.width <= 1) &&
      this.getImageDimensionFromUrl(product.media[0].fullUrl);
  }

  private get signature() {
    return md5(this.props.globals.product.media.reduce((acc, m) => acc.concat(m.url), ''));
  }

  /* istanbul ignore next: todo: test */
  private getImageDimensionFromUrl(url): void {
    const img = new Image();
    img.onload = () => {
      this.imageDimensionFallback = {height: img.naturalHeight, width: img.naturalWidth};
    };
    img.src = url;
  }

  private getRootStyles(): React.CSSProperties {
    const {mainMedia} = this.props.layoutConfig.dimensions;
    const {media} = this.props.globals.product;

    const nativeMediaAspectRatio = media[0]?.width && media[0]?.height ? media[0].width / media[0].height : 'initial';

    return {
      '--fixedMediaWidth': `${mainMedia.widthConf.num}${mainMedia.widthConf.unit}`,
      '--fixedMediaHeight': `${mainMedia.heightConf.num}${mainMedia.heightConf.unit}`,
      '--nativeMediaAspectRatio': nativeMediaAspectRatio,
    } as React.CSSProperties;
  }

  private buildMainMediaContainerClassName(
    isModalZoomEnabled: boolean,
    withImageRatio: boolean,
    isMainMediaNavArrowsEnabled: boolean,
    ribbonsInProductPageEnabled: boolean
  ): string {
    const {useExperiments} = this.props.globals;
    const fixProductPageMediaCentering = useExperiments.enabled(SPECS.FixProductPageMediaCentering);

    return cx({
      'modal-zoom-icon': isModalZoomEnabled,
      [style.relativePositionedMediaWrapper]: ribbonsInProductPageEnabled,
      [style.dynamicMediaDimensions]: withImageRatio,
      [style.fixedMediaDimensions]: !withImageRatio,
      [style.fixProductPageMediaCentering]: fixProductPageMediaCentering,
      [style.isMainMediaNavArrowsEnabled]: isMainMediaNavArrowsEnabled,
    });
  }

  /* istanbul ignore next: todo: test */
  private applyDimensionFallback(media: IProductDTO['media'][0] | undefined): void {
    if (media) {
      media.height = media.height > 0 ? media.height : this.imageDimensionFallback.height;
      media.width = media.width > 0 ? media.width : this.imageDimensionFallback.width;
    }
  }

  public render(): JSX.Element {
    const {
      layoutConfig: {withImageRatio, swipeToScroll, dimensions, withDynamicHeight, align},
      imageRatioId,
      imageMode,
      globals: {
        shouldShowWishlistButton,
        product,
        useExperiments,
        experiments: {renderProductPageSlots, mainMediaWrapperAsAnchorElement, mediaSlotOnBottomForResponsiveGallery},
        slots,
      },
      layout,
    } = this.props;

    const keyMedia = product.media[0];
    const layoutDimensions = dimensions.mainMedia;
    const withProductMediaNavigation = product.media.length > 1;
    const isPluginInstalledInMediaSlot = !!slots?.[ProductPageSlotIds.ProductPageMedia1];
    const isMainMediaNavArrowsEnabled = useExperiments.enabled(SPECS.ProductPageMainMediaNavigationArrows);
    const ribbonsInProductPageEnabled = useExperiments.enabled(SPECS.ShowMultiRibbonsInProductPage);
    this.applyDimensionFallback(keyMedia);

    if (withImageRatio) {
      const {
        ratio: {width, height},
      } = getMainImageRatio(keyMedia, imageRatioId);
      layoutDimensions.heightConf = {
        num: Math.ceil(layoutDimensions.widthConf.num * (height / width)),
        unit: 'px',
      };
    }

    /* istanbul ignore next */
    const MainMediaWrapperElement = mainMediaWrapperAsAnchorElement ? `button` : 'div';

    return (
      <div data-hook={ProductGalleryLayoutDataHook.Root} style={this.getRootStyles()} className={style.root}>
        <ProductGalleryProvider
          withImageRatio={withImageRatio}
          imageRatioId={imageRatioId}
          media={product.media}
          key={this.signature}>
          <ProductGalleryContext.Consumer>
            {({selectedIndex}) => {
              return (
                <ModalGalleryLayout externalSelectedIndex={selectedIndex}>
                  {(modalZoom) => {
                    const mainMediaContainerClassName = this.buildMainMediaContainerClassName(
                      modalZoom.isEnabled,
                      withImageRatio,
                      isMainMediaNavArrowsEnabled,
                      ribbonsInProductPageEnabled
                    );
                    const shouldRenderMainMediaNavigationArrows =
                      isMainMediaNavArrowsEnabled && product.media.length > 1;
                    const mainMedia = (
                      <MainMedia
                        imageMode={imageMode}
                        swipeToScroll={swipeToScroll}
                        product={product}
                        layoutDimensions={layoutDimensions}
                        withDynamicHeight={withDynamicHeight}
                        isZoomModalEnabled={modalZoom.isEnabled}
                      />
                    );

                    if (
                      mainMediaWrapperAsAnchorElement &&
                      (shouldRenderMainMediaNavigationArrows || ribbonsInProductPageEnabled)
                    ) {
                      return (
                        <div
                          data-hook={ProductGalleryLayoutDataHook.MainMediaContainer}
                          onClick={modalZoom.open}
                          className={mainMediaContainerClassName}>
                          <button type="button" tabIndex={-1} className={style.mainMediaInteractiveButton}>
                            {mainMedia}
                          </button>
                          {shouldRenderMainMediaNavigationArrows && <MediaNavigationArrows />}
                          {ribbonsInProductPageEnabled && <MediaOverlay />}
                        </div>
                      );
                    }

                    return (
                      <MainMediaWrapperElement
                        data-hook={ProductGalleryLayoutDataHook.MainMediaContainer}
                        {...(mainMediaWrapperAsAnchorElement && {type: 'button'})}
                        tabIndex={-1}
                        className={mainMediaContainerClassName}
                        onClick={modalZoom.open}>
                        {mainMedia}
                        {shouldRenderMainMediaNavigationArrows && <MediaNavigationArrows />}
                        {ribbonsInProductPageEnabled && <MediaOverlay />}
                      </MainMediaWrapperElement>
                    );
                  }}
                </ModalGalleryLayout>
              );
            }}
          </ProductGalleryContext.Consumer>
          {withProductMediaNavigation && <ProductGalleryNavigation align={align} />}
        </ProductGalleryProvider>
        {!ribbonsInProductPageEnabled && shouldShowWishlistButton && !renderProductPageSlots && (
          <ShowOnMobileOnly>
            <WishlistButton mode={Mode.FLOATING} />
          </ShowOnMobileOnly>
        )}
        {renderProductPageSlots && layout !== Layout.Stunning && (
          <>
            {!ribbonsInProductPageEnabled && (
              <ShowOnDesktopOnly>
                <div className={cx({[style.mediaSlotsContainer]: isPluginInstalledInMediaSlot})}>
                  <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageMedia1} />
                </div>
              </ShowOnDesktopOnly>
            )}
            {(mediaSlotOnBottomForResponsiveGallery || !ribbonsInProductPageEnabled) && (
              <ShowOnMobileOnly>
                <div
                  className={cx({
                    [style.mediaSlotBottom]: mediaSlotOnBottomForResponsiveGallery,
                    [style.mediaSlotsContainer]: !mediaSlotOnBottomForResponsiveGallery && isPluginInstalledInMediaSlot,
                  })}>
                  <SlotsPlaceholder slotId={ProductPageSlotIds.ProductPageMedia1} />
                </div>
              </ShowOnMobileOnly>
            )}
          </>
        )}
      </div>
    );
  }
}

export const ProductGalleryLayout = withGlobalProps(ProductGalleryLayoutComponent);
